class DiscountSection extends HTMLElement {
    constructor() {
        super();

        // Internal elements
        this.input = this.querySelector("#discount-code-input");
        this.button = this.querySelector("#apply-discount-button");
        this.notice = this.querySelector(".discount-section__notice");
        this.noticeText = this.notice.querySelector("#notice-text");

        // External elements
        this.appliedDiscountsContainer = this.querySelector(".applied-discounts");
        this.freeDeliveryInfo = document.querySelector(".free_delivery_info");

        // State
        this.cartId = null;
        this._appliedDiscounts = [];
        this._isLoading = false;
    }

    get appliedDiscounts() {
        return this._appliedDiscounts;
    }

    set appliedDiscounts(value) {
        this._appliedDiscounts = value;
        this._renderAppliedDiscounts();
        this._checkForDeliveryDiscount();
    }

    get isLoading() {
        return this._isLoading;
    }

    set isLoading(value) {
        this._isLoading = value;
        this._toggleLoadingState(value);
    }

    connectedCallback() {
        this.isInDrawer = !!this.closest("cart-drawer");

        this._setupEventListeners();
        this._getAppliedDiscounts();
        this._getInitialAppliedCode();
    }

    disconnectedCallback() {
        //* The drawer footer gets replaced on refresh, so drop the old subscription
        this.cartUpdateUnsubscriber?.();
    }

    _setupEventListeners() {
        this.button.addEventListener("click", (event) => {
            event.preventDefault();
            this._applyDiscountCode();
        });

        // subscribe to pub/sub cart update event
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            //* Fetch re-rendered external containers
            this.appliedDiscountsContainer = this.querySelector(".applied-discounts");
            this.freeDeliveryInfo = document.querySelector(".free_delivery_info");

            this._getAppliedDiscounts();
        });
    }

    _getInitialAppliedCode() {
        const appliedCode = new URLSearchParams(window.location.search).get("applied");
        if (appliedCode) {
            this._showResultNotice(true, appliedCode);

            const url = new URL(window.location);
            url.searchParams.delete("applied");
            window.history.replaceState({}, document.title, url.toString());
        }
    }

    async _getAppliedDiscounts() {
        const cartDataResponse = await fetch("/cart.js");
        const cartData = await cartDataResponse.json();
        const cartId = cartData.token;

        if (!cartId) {
            console.error("No cart ID found.");
        }

        this.cartId = cartId;

        const cartResponse = await fetch(shopifyStorefrontApiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Shopify-Storefront-Access-Token": shopifyStorefrontApiToken,
            },
            body: JSON.stringify({
                query: `
                        query cart($cartId: ID!) {
                            cart(id: $cartId) {
                                id
                                discountCodes {
                                    applicable
                                    code
                                }
                            }
                        }
                    `,
                variables: {
                    cartId: `gid://shopify/Cart/${cartId}`,
                },
            }),
        });

        const cartResponseData = await cartResponse.json();
        this.appliedDiscounts = cartResponseData.data.cart.discountCodes.filter((dc) => dc.applicable).map((dc) => dc.code);
    }

    async _applyDiscountCode() {
        this._hideResultNotice();
        this.isLoading = true;

        const inputCode = this.input.value;

        try {
            const cartResponse = await fetch(shopifyStorefrontApiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token": shopifyStorefrontApiToken,
                },
                body: JSON.stringify({
                    query: `
                    mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
                        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                            cart {
                                id
                                discountCodes {
                                    applicable
                                    code
                                }
                            }
                            userErrors {
                                field
                                message
                            }
                        }
                    }
                    `,
                    variables: {
                        cartId: `gid://shopify/Cart/${this.cartId}`,
                        discountCodes: [this.input.value, ...this.appliedDiscounts],
                    },
                }),
            });

            const cartResponseData = await cartResponse.json();
            this.appliedDiscounts = cartResponseData.data.cartDiscountCodesUpdate.cart.discountCodes.filter((dc) => dc.applicable).map((dc) => dc.code);

            if (this.appliedDiscounts.includes(inputCode)) {
                this._showResultNotice(true, inputCode);

                if (this.isInDrawer) {
                    await this._refreshDrawer();
                    document.querySelector("cart-drawer discount-section")?._showResultNotice(true, inputCode);
                    return;
                }

                const url = new URL(window.location);
                url.searchParams.set("applied", inputCode);
                window.location.href = url.toString();
            } else {
                this._showResultNotice(false, inputCode);
            }
        } catch (error) {
            console.error("Error applying discount code:", error);
            this._showResultNotice(false, inputCode);
        } finally {
            this.isLoading = false;
            this.input.value = "";
        }
    }

    async _removeDiscountCode(codeToRemove) {
        this.isLoading = true;

        try {
            const updatedCodes = this.appliedDiscounts.filter((code) => code !== codeToRemove);

            const cartResponse = await fetch(shopifyStorefrontApiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token": shopifyStorefrontApiToken,
                },
                body: JSON.stringify({
                    query: `
                    mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
                        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                            cart {
                                id
                                discountCodes {
                                    applicable
                                    code
                                }
                            }
                            userErrors {
                                field
                                message
                            }
                        }
                    }
                    `,
                    variables: {
                        cartId: `gid://shopify/Cart/${this.cartId}`,
                        discountCodes: updatedCodes,
                    },
                }),
            });

            const cartResponseData = await cartResponse.json();
            this.appliedDiscounts = cartResponseData.data.cartDiscountCodesUpdate.cart.discountCodes.filter((dc) => dc.applicable).map((dc) => dc.code);

            if (this.isInDrawer) {
                await this._refreshDrawer();
                return;
            }

            window.location.href = window.location.pathname;
        } catch (error) {
            console.error("Error removing discount code:", error);
        } finally {
            this.isLoading = false;
        }
    }

    async _refreshDrawer() {
        //* Re-render drawer items + footer (replaces this element), then recalculate deposit/subtotals like a page load would
        await document.querySelector("cart-drawer-items")?.refreshDrawerCart();
        await validateCart();
    }

    _showResultNotice(success, code) {
        if (success) {
            this.noticeText.textContent = `Kortingscode "${code}" succesvol toegepast!`;
            this.notice.classList.remove("notice--error");
            this.notice.classList.add("notice--success");
        } else {
            this.noticeText.textContent = `Kortingscode is ongeldig. Probeer het opnieuw.`;
            this.notice.classList.remove("notice--success");
            this.notice.classList.add("notice--error");
        }

        this.notice.classList.remove("hidden");
    }

    _hideResultNotice() {
        this.notice.classList.add("hidden");
    }

    _toggleLoadingState(isLoading) {
        if (isLoading) {
            this.button.disabled = true;
            this.button.classList.add("loading");
        } else {
            this.button.disabled = false;
            this.button.classList.remove("loading");
        }
    }

    _renderAppliedDiscounts() {
        const discountListContainer = this.appliedDiscountsContainer.querySelector("#discount-list");
        discountListContainer.innerHTML = "";

        if (this.appliedDiscounts.length > 0) {
            this.appliedDiscountsContainer.classList.remove("hidden");

            this.appliedDiscounts.forEach((code) => {
                const discountItem = `<div class="applied-discounts__item">
                                        <p class="code">${code}</p>
                                        <span class="remove-discount">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x-icon lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                        </span>
                                    </div>`;
                discountListContainer.insertAdjacentHTML("beforeend", discountItem);

                discountListContainer.lastElementChild.addEventListener("click", () => {
                    console.log("Remove discount code", code);
                    this._removeDiscountCode(code);
                });
            });
        } else {
            this.appliedDiscountsContainer.classList.add("hidden");
        }
    }

    _checkForDeliveryDiscount() {
        const hasDeliveryDiscount = this.appliedDiscounts.some((code) => code.toLowerCase().includes("gratisbezorging"));

        if (!hasDeliveryDiscount) return;

        const deliveryElement = document.querySelector(".delivery_line");
        const deliveryCostText = deliveryElement.querySelector("span");

        if (deliveryCostText) {
            deliveryCostText.classList.add("discounted");
            deliveryCostText.textContent = "Gratis";
            if (this.freeDeliveryInfo) {
                if (!this.freeDeliveryInfo.classList.contains("under_minimum_order")) {
                    this.freeDeliveryInfo.classList.add("hidden");
                }
            }
        }
    }
}

customElements.define("discount-section", DiscountSection);
